import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { withPage } from "../../components/Page";
import { PrototypeBar } from "../../components/PrototypeBar";
import { QrLabelModal } from "../../components/QrLabelModal";
import { PhoneScannerModal } from "../../components/PhoneScannerModal";
import { QrCodeSvg } from "../../components/QrCodeSvg";
import styles from "./AssetDetail.module.scss";

const AssetDetail: FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "documents" | "drawings" | "certificates"
  >("documents");

  return (
    <main className={styles.container} tabIndex={-1}>
      <PrototypeBar />

      <div className={styles.pageTitle}>
        <div>
          <div className={styles.eyebrow}>Asset data / Mechanical</div>
          <h1 className={styles.title}>AHU-003</h1>
          <p className={styles.subtitle}>Air Handling Unit · Plant Room 01</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setIsScannerOpen(true)}
          >
            📱 Test QR code scan
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => navigate("/asset-data/ahu-003/qr")}
          >
            ▦ QR code
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => navigate("/contextual-ai")}
          >
            ✦ Ask AI about this asset
          </button>
        </div>
      </div>

      <div className={`${styles.card} ${styles.connectedAssetBanner}`}>
        <div className={styles.bannerContent}>
          <div className={styles.bannerIconBox}>
            <span aria-hidden="true">▦</span>
          </div>
          <div style={{ flex: 1 }}>
            <div className={styles.bannerHeaderRow}>
              <div className={styles.bannerEyebrow}>Connected asset</div>
              <span className={styles.conceptBadge}>
                THIS IS A CONCEPT — Click on the Prototype screens above to
                explore this concept
              </span>
            </div>
            <h3 className={styles.bannerTitle}>
              Scannable asset code concept - this asset is connected to Pulse
            </h3>
            <p className={styles.bannerText}>
              This Pulse QR code links this physical asset directly to its
              digital information. Scan the code on the equipment with your
              phone camera to quickly access asset details, documents, drawings,
              maintenance information and related records.
            </p>
            <div className={styles.flowChain}>
              <span>Physical equipment</span>
              <span className={styles.arrow}>→</span>
              <span>QR code scan</span>
              <span className={styles.arrow}>→</span>
              <span>AHU-003 Digital record</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.card} ${styles.summaryCard}`}>
        <div className={styles.assetSummary}>
          <div className={styles.assetHead}>
            <div className={styles.assetIcon}>AHU</div>
            <div>
              <h2 className={styles.assetHeading}>
                AHU-003{" "}
                <span className={styles.statusBadge}>
                  <span className={styles.dot} />
                  Operational
                </span>
              </h2>
              <p className={styles.assetSub}>Trane Climate Changer · CCU-240</p>
              <p className={styles.assetSub}>
                Building A · Level 01 · Plant Room 01
              </p>
            </div>
          </div>
          <div className={styles.summaryGrid}>
            <div>
              <span className={styles.label}>Asset reference</span>
              <span className={styles.value}>MECH-AHU-003</span>
            </div>
            <div>
              <span className={styles.label}>Serial number</span>
              <span className={styles.value}>SN784563</span>
            </div>
            <div>
              <span className={styles.label}>Commissioned</span>
              <span className={styles.value}>18 Apr 2016</span>
            </div>
            <div>
              <span className={styles.label}>Next service</span>
              <span className={styles.value}>14 Oct 2026</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.grid2}>
        <div>
          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Asset information</h2>
            <p className={styles.sectionDesc}>
              Core identification and operational information retained from the
              handover record.
            </p>
            <div className={styles.facts}>
              <div className={styles.fact}>
                <span className={styles.label}>Manufacturer</span>
                <b>Trane</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Model</span>
                <b>Climate Changer CCU-240</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Air volume</span>
                <b>24,000 l/s</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Supply / extract</span>
                <b>100% fresh air</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Controls</span>
                <b>Trend IQ4E</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Associated panel</span>
                <b>MCC-PL01-03</b>
              </div>
            </div>
          </div>

          <div className={`${styles.card} ${styles.section} ${styles.noMarginBottom}`}>
            <h2 className={styles.sectionTitle}>Information and documents</h2>
            <div className={styles.tabs}>
              <button
                type="button"
                className={activeTab === "documents" ? styles.activeTab : ""}
                onClick={() => setActiveTab("documents")}
              >
                Documents (6)
              </button>
              <button
                type="button"
                className={activeTab === "drawings" ? styles.activeTab : ""}
                onClick={() => setActiveTab("drawings")}
              >
                Drawings (2)
              </button>
              <button
                type="button"
                className={activeTab === "certificates" ? styles.activeTab : ""}
                onClick={() => setActiveTab("certificates")}
              >
                Certificates (1)
              </button>
            </div>

            <div className={styles.tableWrapper}>
            {activeTab === "documents" && (
              <>
                <div className={styles.docRow}>
                  <div className={styles.file}>PDF</div>
                  <div>
                    <b>AHU-003 Operation &amp; Maintenance Manual</b>
                    <small>Trane · O&amp;M manual · 4.2 MB</small>
                  </div>
                  <a
                    href="/more-info"
                    className={styles.openLink}
                  >
                    Open ↗
                  </a>
                </div>
                <div className={styles.docRow}>
                  <div className={styles.file}>PDF</div>
                  <div>
                    <b>AHU-003 Planned Maintenance Schedule</b>
                    <small>Pulse · Maintenance · Rev 03</small>
                  </div>
                  <a
                    href="/more-info"
                    className={styles.openLink}
                  >
                    Open ↗
                  </a>
                </div>
              </>
            )}

            {activeTab === "drawings" && (
              <div className={styles.docRow}>
                <div className={styles.file}>DWG</div>
                <div>
                  <b>Plant Room 01 Mechanical Services Layout</b>
                  <small>Drawings · M-PL01-200</small>
                </div>
                <a
                  href="/more-info"
                  className={styles.openLink}
                >
                  Open ↗
                </a>
              </div>
            )}

            {activeTab === "certificates" && (
              <div className={styles.docRow}>
                <div className={styles.file}>PDF</div>
                <div>
                  <b>AHU-003 Electrical &amp; Pressure Certificate</b>
                  <small>Certification · Rev 01</small>
                </div>
                <a
                  href="/more-info"
                  className={styles.openLink}
                >
                  Open ↗
                </a>
              </div>
            )}
            </div>
          </div>
        </div>

        <aside className={styles.asideCol}>
          <div
            className={`${styles.card} ${styles.section} ${styles.maintenanceCallout}`}
          >
            <h3>Maintenance attention</h3>
            <p>
              Quarterly filter inspection is due in 23 days. Review the
              scheduled task before the next planned visit.
            </p>
          </div>

          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Asset activity</h2>
            <div className={styles.kpis}>
              <div className={styles.kpi}>
                <span>Documents</span>
                <b>9</b>
              </div>
              <div className={styles.kpi}>
                <span>Drawings</span>
                <b>2</b>
              </div>
              <div className={styles.kpi}>
                <span>Records</span>
                <b>14</b>
              </div>
            </div>
            <ul className={styles.linkList}>
              <li>
                <b>Last inspection completed</b>
                <span>14 Jul 2026 · J. Roberts</span>
              </li>
              <li>
                <b>Filter set replaced</b>
                <span>18 Apr 2016 · Service record</span>
              </li>
            </ul>
          </div>

          <div
            className={styles.qrPreviewCard}
            onClick={() => setIsScannerOpen(true)}
            role="button"
            tabIndex={0}
            title="Click to try scanning with your phone camera"
          >
            <b>Pulse Asset QR</b>
            <div className={styles.qrGraphic}>
              <QrCodeSvg />
            </div>
            <small>Tap to scan with phone camera</small>
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            style={{
              width: "100%",
              marginTop: "1rem",
              fontSize: "1.2rem",
              height: "3.6rem",
            }}
            onClick={() => setIsScannerOpen(true)}
          >
            📱 Scan with phone camera (Live demo)
          </button>
        </aside>
      </div>

      <QrLabelModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type="asset"
      />
      <PhoneScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
      />
    </main>
  );
};

export default withPage(AssetDetail)({
  meta: {
    title: "AHU-003 — Asset Data · Pulse",
    description: "Air Handling Unit AHU-003 record in Plant Room 01.",
  },
  breadcrumbs: [
    { label: "Pulse", href: "/" },
    { label: "Commerzbank", href: "/" },
    { label: "Asset data", href: "/asset-data/ahu-003" },
  ],
});
